import {
  AsyncPipe
} from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, DOCUMENT, inject } from '@angular/core';
import {MatSidenavModule} from '@angular/material/sidenav';
import {
    ActivatedRoute,
    Event,
    NavigationEnd,
    Router,
    RouterOutlet,
} from '@angular/router';
import {TranslocoService} from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import {differenceInDays} from 'date-fns';
import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    interval,
    Observable,
    switchMap,
    timer,
} from 'rxjs';
import {
    map,
    startWith,
    tap,
} from 'rxjs/operators';

import {
    ModalLockedFeatureMode,
} from '~/app/domains/authorisation/components/modal-locked-feature/modal-locked-feature.component';
import {ModalConfirmationV2Component} from '~/app/shared/modals/v2/modal-confirmation/modal-confirmation.component';
import {PortfolioState} from '~/app/domains/portfolios/enums/portfolios-state.enum';
import {
    ModalOnboardingNewMemberComponent,
} from '~/app/domains/authorisation/components/modal-onboarding-new-member/modal-onboarding-new-member.component';
import {ResumeFeatureService} from '~/app/domains/authorisation/services/resume-feature/resume-feature.service';
import {UNLOCKED_FEATURE_OPERATION_ID} from '~/app/shared/constants/operation-resume-unlocked-feature.constants';
import {PERMISSIONS} from '~/app/domains/authorisation/constants/permissions.constants';
import {ValidPhoneNumberComponent} from '~/app/shared/modals/valid-phone-number/valid-phone-number.component';
import {AuthenticationService} from '~/app/core/services/api/authentication/authentication.service';
import {ApplicationFacade} from '~/app/core/state/application/application-facade/application.facade';
import {AuthenticationFacade} from '~/app/core/state/authentication/authentication.facade';
import {AuthorisationFacade} from '~/app/core/state/authorisation/authorisation.facade';
import {ConfigurationFacade} from '~/app/core/state/configuration/configuration.facade';
import {SidebarComponent} from '~/app/shared/components/menu/sidebar/sidebar.component';
import {FeatureFlag} from '~/app/domains/configuration/enums/feature-flag.enum';
import {RESUME_FEATURES} from '~/app/shared/enums/resume-features.enum';
import {ModalRef} from '~/app/shared/services/modal/modal-ref';
import {ModalService} from '~/app/shared/services/modal/modal.service';
import {Menu} from '~/app/shared/types/api/menu.type';
import {SidebarItemAuthorisation} from '~/app/shared/types/sidebar-item-authorisation.type';
import {SidebarItem} from '~/app/shared/types/sidebar-item.type';
import {User} from '~/app/shared/types/user/user.type';
import packageInfo from "../../../../package.json";
import {toSignal} from "@angular/core/rxjs-interop";
import {ModalProcessingComponent} from '~/app/shared/modals/modal-processing/modal-processing.component';
import {
    OnboardingNewFeatureComponent
} from "~/app/domains/onboarding/modals/onboarding-new-feature-modal/onboarding-new-feature.component";
import {TUTORIAL_MENU_ID} from "~/app/shared/constants/menu-item-id.constant";
import {HELP_MENU_CODE} from "~/app/shared/constants/menu-item-code.constant";
import {TRACKING_ACTIONS} from "~/app/shared/enums/tracking/matomo-action.enum";
import {MenuItemsOpenableInModal} from "~/app/domains/onboarding/enums/menu-item-open-in-modal.enum";
import {OnboardingFacade} from "~/app/domains/onboarding/onboarding.facade";
import {SupademoAutoLaunchDirective} from "~/app/domains/onboarding/directives/supademo-auto-launch.directive";
import {
    PortfoliosDetailsStateFacade
} from "~/app/core/state/portfolios-details/portfolios-details-state-facade.service";
import {HelpFacade} from "~/app/domains/help/help.facade";
import {ModalProfessionalInfoService} from "~/app/domains/profile/services/modal-professional-info.service";
import {shouldOpenProfileIncompletePopin} from "~/app/domains/profile/utils/should-open-profile-incomplete-popin";

@UntilDestroy()
@Component({
    selector: 'eb-main-layout',
    templateUrl: './main-layout.component.html',
    styleUrls: ['./main-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SidebarComponent,
        MatSidenavModule,
        RouterOutlet,
        AsyncPipe,
        SupademoAutoLaunchDirective,
    ]
})
export class MainLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    private router = inject(Router);
    private transloco = inject(TranslocoService);
    private activatedRoute = inject(ActivatedRoute);
    private resumeFeatureService = inject(ResumeFeatureService);
    applicationFacade = inject(ApplicationFacade);
    private authenticationService = inject(AuthenticationService);
    configurationFacade = inject(ConfigurationFacade);
    authFacade = inject(AuthenticationFacade);
    authorisationFacade = inject(AuthorisationFacade);
    private document = inject<Document>(DOCUMENT);
    private modal = inject(ModalService);
    private onboardingFacade = inject(OnboardingFacade);
    private helpFacade = inject(HelpFacade);
    private portfoliosDetailsFacade = inject(PortfoliosDetailsStateFacade);
    private modalProfessionalInfoService = inject(ModalProfessionalInfoService);

    public token = toSignal(this.authFacade.token$);
    public showNewFeaturesPopup = toSignal(this.applicationFacade.showNewFeaturesPopup$);
    public hideProfileIncompletePopin = toSignal(this.applicationFacade.hideProfileIncompletePopin$);
    public applicationConfiguration = toSignal(this.configurationFacade.getConfiguration());

    protected readonly version = packageInfo.version;

    public options = [
        {id: 'Option H1', label: 'Option 1', selected: false, disable: true},
        {id: 'Option H13', label: 'Option 13', selected: false},
        {id: 'Option H12', label: 'Option 12', selected: false},
    ];

    public expanded = true;

    public scrollDetectionEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private menuContext$ = combineLatest([
        this.router.events.pipe(
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
            startWith({ url: this.router.url } as NavigationEnd),
            map((event) => event.url),
        ),
        this.portfoliosDetailsFacade.portfolioClientId$,
    ]);

    // A portfolio attached to a contact lives under /portfolios/* but conceptually belongs
    // to the contacts module — keep the sidebar anchored on "Mes contacts > Clients".
    private isInClientPortfolioContext(url: string, clientId: number | null): boolean {
        const isPortfolioCreateOrDetails = url.startsWith('/portfolios/create')
            || url.startsWith('/portfolios/details/');
        return isPortfolioCreateOrDetails && clientId !== null;
    }

    public moduleChanged$ = this.menuContext$.pipe(
        map(([url, clientId]) => {
            if (this.isInClientPortfolioContext(url, clientId)) {
                return 'contacts';
            }
            return url.split('/').filter((value) => !!value)?.[0];
        }),
        distinctUntilChanged(),
    );

    public activeChildKey$ = this.menuContext$.pipe(
        map(([url, clientId]) => {
            // All /contacts/* routes belong to the "Clients" submenu while Partners/Colleagues are locked.
            if (url.startsWith('/contacts') || this.isInClientPortfolioContext(url, clientId)) {
                return 'contacts-clients';
            }
            return null;
        }),
        distinctUntilChanged(),
    );

    public expandedSubmenuKey$ = this.menuContext$.pipe(
        map(([url, clientId]) => {
            if (url.startsWith('/contacts') || this.isInClientPortfolioContext(url, clientId)) {
                return 'contacts';
            }
            return null;
        }),
        distinctUntilChanged(),
    );

    public mainItems$: Observable<SidebarItem[]> = combineLatest([
        this.transloco.selectTranslateObject('common.sidebar'),
        this.moduleChanged$,
    ]).pipe(
        map(([translations, module]: [Record<string, string>, string]) => [
            {
                key: 'funds',
                label: translations.funds,
                icon: 'ui/nav/funds',
                selected: module === 'shares',
                action: () => this.navigateWithPortfolioGuard('shares/list'),
                permissions: [],
            },
            ...(
                this.configurationFacade.getFeatureFlagByFeatureFlagNameSnapshot(
                    FeatureFlag.TECH_FRONT_PORTFOLIO_ENABLE,
                )
                    ? [
                        {
                            key: 'portfolios',
                            label: translations.portfolios_model,
                            icon: 'ui/nav/ptf_model',
                            selected: module === 'portfolios',
                            permissions: [PERMISSIONS.PORTFOLIO_MODULE_ACCESS],
                            resumeFeature: RESUME_FEATURES.PORTFOLIO_MODULE,
                            action: () => this.navigateWithPortfolioGuard('portfolios'),
                        },
                    ] as SidebarItemAuthorisation[]
                    : []
            ),
            {
                key: 'contacts',
                label: translations.contacts,
                icon: 'ui/nav/contact',
                selected: module === 'contacts',
                permissions: [PERMISSIONS.CONTACT_MANAGE_ACCESS],
                resumeFeature: RESUME_FEATURES.CONTACT_MODULE,
                children: [
                    {
                        key: 'contacts-clients',
                        label: translations.contacts_clients ?? 'Clients',
                        action: () => this.navigateWithPortfolioGuard('/contacts/list?category=clients'),
                    },
                    {
                        key: 'contacts-partners',
                        label: translations.contacts_partners ?? 'Partenaires',
                        locked: true,
                        lockedTooltip: translations.contacts_locked_tooltip ?? 'Bientôt disponible',
                    },
                    {
                        key: 'contacts-colleagues',
                        label: translations.contacts_colleagues ?? 'Collègues',
                        locked: true,
                        lockedTooltip: translations.contacts_locked_tooltip ?? 'Bientôt disponible',
                    },
                ],
            },
        ]),
    );

    public userItem$: Observable<SidebarItem> = combineLatest([
        this.transloco.selectTranslateObject('common.sidebar').pipe(startWith({})),
        this.authFacade.user$,
        this.moduleChanged$,
    ]).pipe(
        map(([translations, user]: [Record<string, string>, User | undefined, string]) => ({
            key: 'user',
            label: user ? `${user.firstName} ${user.lastName}` : translations.signin,
            icon: user ? 'ui/nav/user-params' : 'ui/nav/user',
            permissions: [],
            selected: this.router.url.includes('profile'),
            ...(!user ? {action: () => this.navigateWithPortfolioGuard('auth/login')} : {}),
            items: user ? [
                {
                    key: 'user_profile',
                    label: translations.profile,
                    permissions: [],
                    icon: 'ui/nav/user',
                    action: () => this.navigateWithPortfolioGuard('profile'),
                },
                {
                    key: 'user_logout',
                    label: translations.logout,
                    permissions: [],
                    icon: 'ui/nav/user',
                    action: () => {
                        const portfolioState = this.portfoliosDetailsFacade.getPortfolioStateSnapshot();
                        const isOnAllocation = this.router.url.includes('/allocation');
                        if (isOnAllocation && (portfolioState === PortfolioState.CREATING || portfolioState === PortfolioState.UPDATING)) {
                            this.openLeaveAllocationConfirmation(
                                () => void this.router.navigateByUrl('auth/logout', {state: {bypassGuardAndResolver: true}}),
                            );
                            return;
                        }
                        void this.router.navigateByUrl('auth/logout', {state: {bypassGuardAndResolver: true}});
                    },
                },
            ] : [],
        })),
    );

    public deduceIconBasedOnId(menuItemId: number | undefined, menuCode?: string) {
        if (menuCode === HELP_MENU_CODE) {
            return 'ui/nav/help';
        }
        if (menuItemId && menuItemId === TUTORIAL_MENU_ID) {
            return 'ui/nav/video';
        }
        return '';
    }

    public deduceTrackingBasedOnId(menuItemId: number | undefined): TRACKING_ACTIONS | null {
        if (menuItemId && menuItemId === TUTORIAL_MENU_ID) {
            return TRACKING_ACTIONS.TUTORIAL_VIDEOS;
        }
        return null;
    }

    private deduceActionBasedOnCode(menu: Menu): (() => void) | undefined {
        if (menu.code === HELP_MENU_CODE) {
            return () => this.helpFacade.openHelpModal();
        }
        if (menu.url) {
            return () => this.openUrl(menu.url);
        }
        return undefined;
    }

    public otherItems$: Observable<SidebarItem[]> = this.applicationFacade.menu$.pipe(
        map((menus: Menu[]) => menus.map((menu, index) => (
            {
                key: `menu-${index}`,
                label: menu.name,
                isNew: menu.isNew ?? false,
                tracking: this.deduceTrackingBasedOnId(menu.id),
                icon: this.deduceIconBasedOnId(menu.id, menu.code),
                permissions: [],
                action: this.deduceActionBasedOnCode(menu),
                items: menu.children.length ? menu.children.map((submenu, subindex) => (
                    {
                        key: `menu-children-${subindex}`,
                        label: submenu.name,
                        permissions: [],
                        ...(submenu.url ? {action: () => this.openUrl(submenu.url)} : {}),
                    })) : [],
            }))),
    );

    public isSidebarOpen = false;

    public isLoading = true;

    public expandHeaderUrls = [
        '/shares/list',
        '/portfolios',
        '/contacts/list',
    ];

    private modalRef: ModalRef | null = null;

    ngOnInit() {
        if (window.innerWidth <= 1024) {
            this.expanded = false;
        }

        this.applicationFacade.getMenus();

        // Subscribe to processing state
        this.applicationFacade.isProcessing$
            .pipe(
                tap((isProcessing) => {
                    if (isProcessing && !this.modalRef) {
                        // Open modal if processing is true and no modal is open
                        this.modalRef = this.modal.open(
                            ModalProcessingComponent,
                            {},
                            {
                                padding: true,
                                hasBorder: false,
                                isOutsideClickCloseModal: false,
                                closable: false,
                            }
                        );
                    } else if (!isProcessing && this.modalRef) {
                        // Close modal if processing is false and modal is open
                        this.modalRef.close(true);
                        this.modalRef?.dispose();
                        this.modalRef = null;
                    }
                }),
                untilDestroyed(this)
            ).subscribe();

        this.router.events.pipe(
            filter((e: any) => e instanceof NavigationEnd),
            distinctUntilChanged(),
            tap((event) => {

                if (this.expandHeaderUrls.includes(event.url)) {
                    this.scrollDetectionEnabled$.next(false);
                } else {
                    this.scrollDetectionEnabled$.next(true);
                }
            }), untilDestroyed(this),
        ).subscribe();

        combineLatest([
            this.authFacade.user$,
            this.applicationFacade.lastCheckSessionDate$,
            this.configurationFacade.phoneVerificationRequired$,
            interval(1000 * 60 * 60 * 2).pipe(
                startWith(1),
            ), // 2h
        ]).pipe(
            filter(([, , phoneRequired]: [User | undefined, string | null, boolean, number]) => phoneRequired),
            filter(([user]: [User | undefined, string | null, boolean, number]) => !!user),
            filter(([user]) => !user?.phoneVerified),
            filter(([, lastCheckDate]) => lastCheckDate === null
                || differenceInDays(new Date(), new Date(lastCheckDate)) !== 0),
            switchMap(() => this.applicationFacade.updateLastCheckSessionDate(new Date().toISOString())),
            switchMap(() => this.authenticationService.user()),
            filter((user) => user.sessionCount > 1),
            untilDestroyed(this),
        ).subscribe((user) => {
            this.authFacade.updateUser(user);
            this.validPhoneNumber(user.sessionCount);
        });

        const user = this.authFacade.getUserSnapshot();
        if (user && user?.sessionCount > 2) {
            // To force to review modal if user refresh the page
            this.applicationFacade.updateLastCheckSessionDate(null);
        }

        // Reload licence when plan is revoked
        this.authorisationFacade.endValidity$
            .pipe(
                filter((endDate): endDate is string => !!endDate),
                filter((endDate) => differenceInDays(new Date(endDate), new Date()) < 24), // > 24j, timers doesn't work
                switchMap((endDate: string) => timer(new Date(endDate))),
                tap(() => {
                    this.document.defaultView?.location.reload();
                }),
                untilDestroyed(this),
            ).subscribe(() => {
            /* noop - side effects handled in pipe */
        });
    }
    ngOnDestroy(): void {
        this.modalRef?.dispose();
    }

    ngAfterViewInit(): void {
        if (this.applicationFacade.getIsFreemiumOnboardingVisibleSnapshot()
            && this.authFacade.getUserSnapshot()?.sessionCount === 0
            && this.authFacade.getTokenSnapshot()
            && this.authorisationFacade.getPlanSnapshot()?.isFreemium
        ) {
            // Hydratation en tâche de fond (comme avant), la popin freemium 1ʳᵉ visite reste prioritaire et inchangée.
            if (this.authFacade.getUserSnapshot()?.id) {
                this.authFacade.refreshUser().pipe(untilDestroyed(this)).subscribe();
            }

            const userName = this.authFacade.getUserNameSnapshot();
            const resumeFeatureOperation = this.applicationFacade.getOperationSnapshotByOperationId(UNLOCKED_FEATURE_OPERATION_ID);

            this.modalRef = this.modal.open(
                ModalOnboardingNewMemberComponent,
                {
                    resumeFeatureOperation,
                    userName,
                },
                {
                    padding: false,
                    hasBorder: true,
                    isOutsideClickCloseModal: false,
                },
            );

            this.modalRef.afterClosed$
                .pipe(
                    untilDestroyed(this),
                ).subscribe((value) => {
                this.applicationFacade.updateIsFreemiumOnboardingVisible(false);
                this.applicationFacade.deleteOperation(UNLOCKED_FEATURE_OPERATION_ID);

                if (value === 'START_FREE_TRIAL') {
                    this.resumeFeatureService.freeTrial();
                }
            });

            this.openFeaturePromotionPopupIfNeeded();
            return;
        }

        if (this.token()) {
            this.applicationFacade.loadPromotedFeaturesByVersion(packageInfo?.version);
        }

        // EF-3852 : au login normal, la popin de saisie du profil incomplet est prioritaire ;
        // à sa fermeture, on enchaîne la popin de promotion des features.
        this.openProfileIncompletePopinThenPromotion();
    }

    /**
     * EF-3852 — La complétude du profil dépend de champs (`regulatoryRegistrationNumber`, `photoUrl`)
     * absents du payload de login → on rafraîchit le membre (`GET /members/me`) avant de décider.
     * En cas d'échec du fetch, on ne bloque pas : on ouvre directement la popin promo si due.
     */
    private openProfileIncompletePopinThenPromotion(): void {
        if (!this.authFacade.getUserSnapshot()?.id) {
            this.openFeaturePromotionPopupIfNeeded();
            return;
        }

        this.authFacade.refreshUser()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this.decideProfileOrPromotionPopin(),
                error: () => this.openFeaturePromotionPopupIfNeeded(),
            });
    }

    private decideProfileOrPromotionPopin(): void {
        const shouldOpenProfile = shouldOpenProfileIncompletePopin({
            user: this.authFacade.getUserSnapshot(),
            hidePopin: this.applicationFacade.getHideProfileIncompletePopinSnapshot(),
            hasTutorial: !!this.getTutorialParams().demoId,
        });

        if (!shouldOpenProfile) {
            this.openFeaturePromotionPopupIfNeeded();
            return;
        }

        const profileModalRef = this.modalProfessionalInfoService.open();
        profileModalRef.afterClosed$
            .pipe(untilDestroyed(this))
            .subscribe(() => this.openFeaturePromotionPopupIfNeeded());
    }

    private openFeaturePromotionPopupIfNeeded(): void {
        const tutorialParams = this.getTutorialParams();

        if (this.showNewFeaturesPopup() && !tutorialParams.demoId) {
            this.modalRef = this.modal.open(
                OnboardingNewFeatureComponent,
                {},
                {
                    padding: true,
                    hasBorder: true,
                    isOutsideClickCloseModal: true,
                    closable: true,
                    overflow: false,
                }
            );
            this.modalRef.afterClosed$
                .pipe(
                    tap(() => {
                        this.applicationFacade.setNewFeaturesPopupShowed(false);
                    }),
                    untilDestroyed(this),
                ).subscribe()
        }
    }

    private getTutorialParams() {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        return {
            demoId: queryParams['tutorial'],
            supademoOptions: queryParams['supademoOptions'],
        };
    }
    public async closeSideNav(): Promise<void> {
        const parent = this.router.url.split('/').filter((value) => !!value)?.[0];
        const parentPath = `/${parent || 'shares'}`;

        await this.router.navigate([parentPath, {
            outlets: {
                sideFull: null,
            },
        }], {
            relativeTo: this.activatedRoute,
            replaceUrl: true,
        });
    }

    public async closeSideNavALT(preserveQueryParams: Record<string, any> = {}): Promise<void> {
        const parent = this.router.url.split('/').filter((value) => !!value)?.[0];
        const parentPath = `/${parent || 'shares'}`;

        // Combiner les queryParams actuels avec ceux à préserver
        const currentQueryParams = this.activatedRoute.snapshot.queryParams;
        const queryParams = {...currentQueryParams, ...preserveQueryParams};

        await this.router.navigate([parentPath, {
            outlets: {
                sideFull: null,
            },
        }], {
            queryParams: queryParams,
            relativeTo: this.activatedRoute,
            replaceUrl: true,
        });
    }

    public validPhoneNumber(count: number) {
        return this.modal.open(
            ValidPhoneNumberComponent,
            {
                sessionCount: count,
            },
            {
                padding: true,
                isOutsideClickCloseModal: count <= 2,
                closable: count < 3,
            },
        );
    }

    public signup() {
        if (this.applicationFacade.getOperationSnapshotByOperationId(UNLOCKED_FEATURE_OPERATION_ID)) {
            this.applicationFacade.deleteOperation(UNLOCKED_FEATURE_OPERATION_ID);
        }
        void this.router.navigateByUrl('auth/signup');
    }

    public freetrial() {
        this.resumeFeatureService.openLockedFeatureModalByContext(ModalLockedFeatureMode.UPGRADE_TO_FREE_TRIAL, false);
    }

    @HostListener('window:resize')
    public onWindowResize(): void {
        if (window.innerWidth <= 800 && this.expanded) {
            this.expanded = false;
        } else if (window.innerWidth > 1024 && !this.expanded) {
            this.expanded = true;
        }
    }

    public toggleMenu() {
        this.expanded = !this.expanded;
    }

    public openFeaturesPopup(): void {
        this.applicationFacade.forceShowPromotedFeaturesForVersion(packageInfo.version);
        this.modal.open(
            OnboardingNewFeatureComponent,
            {},
            {
                padding: true,
                hasBorder: true,
                isOutsideClickCloseModal: true,
                closable: true,
                overflow: false,
            }
        );
    }

    private navigateWithPortfolioGuard(url: string): void {
        const portfolioState = this.portfoliosDetailsFacade.getPortfolioStateSnapshot();
        const isOnAllocation = this.router.url.includes('/allocation');

        if (isOnAllocation && (portfolioState === PortfolioState.CREATING || portfolioState === PortfolioState.UPDATING)) {
            this.openLeaveAllocationConfirmation(() => void this.router.navigateByUrl(url));
            return;
        }

        void this.router.navigateByUrl(url);
    }

    private openLeaveAllocationConfirmation(onConfirm: () => void): void {
        const portfolioState = this.portfoliosDetailsFacade.getPortfolioStateSnapshot();
        const messageKey = portfolioState === PortfolioState.CREATING
            ? 'portfolios_details.cancel_portfolio_creation'
            : 'portfolios_details.cancel_portfolio_modification';

        this.modal.open(ModalConfirmationV2Component, {
            icon: 'ui/label/notif',
            message: this.transloco.translate(messageKey),
            descriptionSentences: [
                this.transloco.translate('proposal.cancel_popin.subtitle'),
            ],
            buttons: [
                {
                    title: this.transloco.translate('common.no'),
                    callback: (instance: ModalConfirmationV2Component) => {
                        instance.close(false);
                    },
                    color: 'secondary',
                    buttonPriority: 'secondary',
                },
                {
                    title: this.transloco.translate('common.yes'),
                    callback: (instance: ModalConfirmationV2Component) => {
                        onConfirm();
                        instance.close(true);
                    },
                    color: 'primary-v2',
                    buttonPriority: 'primary',
                },
            ],
        });
    }

    private async openUrl(url: string | null) {
        if(url) {
            if(MenuItemsOpenableInModal.some((value) => url?.includes(value))) {

                const hubId: string | undefined = this.applicationConfiguration()?.applicationConfiguration?.supademoHubId;
                if(hubId) {
                    await this.onboardingFacade.openSupaDemoDemoHubById(hubId);
                }

            } else {
                this.document.defaultView?.open(url, '_blank');
            }
        }
    }
}
